import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import useLabels from 'infrastructure/js/hooks/useLabels';
import { api as aiAssistantApi } from '../../../actions/AiAssistant/aiAssistantActions';
import { loadDraft, saveDraft, loadFontSize, saveFontSize } from '../../../utils/AiAssistant/aiAssistantStorage';
import ClearHistoryIcon from '../../../../assets/svg/ai-clear-history.svg';
import CopyIcon from '../../../../assets/svg/duplicate.svg';
import SendIcon from 'infrastructure/assets/svg/arrow-up-ai.svg';
import RobotIcon from '../../../../assets/svg/ai-agent-robot.svg';
import './aiAssistantChat.scss';

const FONT_SIZES = [11, 12, 13, 14, 15, 16, 17, 18, 20, 22, 24];
const FONT_SIZE_DEFAULT = 16;
const GROUP_THRESHOLD_MS = 2 * 60 * 1000;

const formatTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDateLabel = (timestamp, labels) => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return labels.get('separator.today');
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return labels.get('separator.yesterday');
  }
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

const isLastInGroup = (message, nextMessage) => {
  if (!nextMessage) {
    return true;
  }
  if (nextMessage.get('role') !== message.get('role')) {
    return true;
  }
  const gap = (nextMessage.get('timestamp') || 0) - (message.get('timestamp') || 0);
  return gap > GROUP_THRESHOLD_MS;
};

const markdownComponents = {
  table: ({ children, ...props }) => (
    <div className="ais-chat-table-wrapper">
      <table {...props}>{children}</table>
    </div>
  ),
};

export default function AiAssistantChat({ sData, isVisible = false, onClose }) {
  const [inputText, setInputText] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [fontSize, setFontSize] = useState(() => {
    const stored = loadFontSize();
    return stored && FONT_SIZES.includes(stored) ? stored : FONT_SIZE_DEFAULT;
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sendBtnRef = useRef(null);
  const userClickedDuringLoadingRef = useRef(false);

  const labels = useLabels('mat.ai.assistant.chat.')

  const messages = sData.get('messages');
  const loading = sData.get('loading');
  const streamingStatus = sData.get('streamingStatus');

  useEffect(() => {
    if (!isVisible || !messagesEndRef.current) {
      return;
    }
    messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [isVisible, messages, loading]);

  // Track whether user clicked somewhere while loading, to avoid stealing focus.
  useEffect(() => {
    if (!loading) {
      return;
    }
    userClickedDuringLoadingRef.current = false;
    const handleMouseDown = () => { userClickedDuringLoadingRef.current = true; };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [loading]);

  // When loading finishes, refocus the input if the user hasn't clicked elsewhere.
  useEffect(() => {
    if (loading) {
      return;
    }
    const timer = setTimeout(() => {
      if (inputRef.current && !userClickedDuringLoadingRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [loading]);

  // When the chat becomes visible, load the draft and focus the input.
  useEffect(() => {
    if (!isVisible) {
      return;
    }
    setInputText(loadDraft());
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    const handleDocKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleDocKeyDown);
    return () => document.removeEventListener('keydown', handleDocKeyDown);
  }, [isVisible, onClose]);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    const style = getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight);
    const paddingTop = parseFloat(style.paddingTop);
    const paddingBottom = parseFloat(style.paddingBottom);
    const maxHeight = lineHeight * 5 + paddingTop + paddingBottom;
    const newHeight = Math.min(el.scrollHeight, maxHeight);
    el.style.height = newHeight + 'px';
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';

    if (sendBtnRef.current) {
      const singleRowHeight = lineHeight + paddingTop + paddingBottom;
      sendBtnRef.current.style.height = singleRowHeight + 'px';
    }
  }, [inputText, fontSize]);

  const setInput = (text) => {
    setInputText(text);
    saveDraft(text);
  };

  const handleSend = () => {
    const text = inputText.trim();
    if (!text || loading) {
      return;
    }
    setInput('');
    aiAssistantApi.sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  const handleFontDecrease = () => {
    const idx = FONT_SIZES.indexOf(fontSize);
    if (idx <= 0) {
      return;
    }
    const newSize = FONT_SIZES[idx - 1];
    setFontSize(newSize);
    saveFontSize(newSize);
  };

  const handleFontIncrease = () => {
    const idx = FONT_SIZES.indexOf(fontSize);
    if (idx >= FONT_SIZES.length - 1) {
      return;
    }
    const newSize = FONT_SIZES[idx + 1];
    setFontSize(newSize);
    saveFontSize(newSize);
  };

  const renderMessage = (message, index) => {
    const role = message.get('role');
    const text = message.get('text');
    const timestamp = message.get('timestamp');
    const isStreaming = message.get('streaming');

    if (isStreaming && !text) {
      return null;
    }

    const prevTimestamp = index > 0 ? messages.get(index - 1).get('timestamp') : null;
    const nextMessage = messages.get(index + 1);

    const showDateSeparator = timestamp && (
      !prevTimestamp ||
      new Date(timestamp).toDateString() !== new Date(prevTimestamp).toDateString()
    );
    const showTime = timestamp && !isStreaming && isLastInGroup(message, nextMessage);

    const copyBtn = !isStreaming && (
      <button
        className={`ais-chat-copy-btn${copiedId === index ? '--copied' : ''}`}
        onClick={() => handleCopy(text, index)}
        title="Copy message"
        aria-label="Copy message"
      >
        {copiedId === index ? <i className="fa fa-check" /> : <CopyIcon />}
      </button>
    );

    return (
      <React.Fragment key={index}>
        {showDateSeparator && (
          <div className="ais-chat-date-separator">
            <span>{formatDateLabel(timestamp, labels)}</span>
          </div>
        )}
        <div className={`ais-chat-message ais-chat-message--${role}`}>
          <div className="ais-chat-message-content">
            <div className="ais-chat-bubble-row">
              {role === 'user' && copyBtn}
              {role === 'assistant' && (
                <div className='ais-chat-bubble-assistant-icon'><RobotIcon/></div>
              )}
              <div className="ais-chat-bubble">
                {role === 'assistant' ? (
                  <>
                    <ReactMarkdown remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: false }] ]}
                                   rehypePlugins={[rehypeKatex]} components={markdownComponents}>{text}</ReactMarkdown>
                    {isStreaming && text && <span className="ais-chat-cursor" />}
                  </>
                ) : (
                  text
                )}
              </div>
              {role === 'assistant' && copyBtn}
            </div>
            {showTime && (
              <span
                className="ais-chat-message-time"
                title={new Date(timestamp).toLocaleString()}
              >
                {formatTime(timestamp)}
              </span>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  };

  const renderLoadingIndicator = () => {
    if (!loading) {
      return null;
    }

    return (
      <div className="ais-chat-bubble--typing">
        <span className="ais-chat-typing-dot" />
        <span className="ais-chat-typing-dot" />
        <span className="ais-chat-typing-dot" />
        <span className="ais-chat-status-text">{streamingStatus || labels.get('status.thinking')}</span>
      </div>
    );
  };

  return (
    <div className="ais-chat" style={{ 'fontSize': `${fontSize}px` }}>
      <div className="ais-chat-header">
        <RobotIcon className="ais-chat-header-icon" />
        <div className="ais-chat-title">
          <span className="ais-chat-title-main">{labels.get('header.title')}</span>
          <span className="ais-chat-title-sub">{labels.get('header.subtitle')}</span>
        </div>
        <div className="ais-chat-toolbar">
          <button
            className="ais-chat-toolbar-btn ais-chat-toolbar-btn--font"
            onClick={handleFontDecrease}
            disabled={FONT_SIZES.indexOf(fontSize) === 0}
            title={labels.get('header.button.decreaseFontSize')}
            aria-label="Decrease font size"
          >
            A-
          </button>
          <button
            className="ais-chat-toolbar-btn ais-chat-toolbar-btn--font"
            onClick={handleFontIncrease}
            disabled={FONT_SIZES.indexOf(fontSize) === FONT_SIZES.length - 1}
            title={labels.get('header.button.increaseFontSize')}
            aria-label="Increase font size"
          >
            A+
          </button>
          <div className="ais-chat-toolbar-spacer" />
          <button
            className="ais-chat-toolbar-btn"
            onClick={() => aiAssistantApi.startNewChat()}
            title={labels.get('header.button.clearChatHistory')}
            aria-label="Clear chat history"
          >
            <ClearHistoryIcon />
          </button>
        </div>
        <button className="ais-chat-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      <div className="ais-chat-messages" >
        {messages.size === 0 ? (
          <div className="ais-chat-welcome">
            <div className="ais-chat-welcome-icon">
              <RobotIcon />
            </div>
            <p className="ais-chat-welcome-text">
              {'TPO Agent — your AI production planner. Ask about work orders, schedules, alerts, or bottlenecks.'}
            </p>
          </div>
        ) : (
          messages.map((message, index) => renderMessage(message, index))
        )}
        <div ref={messagesEndRef}/>
      </div>

      <div className="ais-chat-status-area">
        {renderLoadingIndicator()}
      </div>

      <div className="ais-chat-input-area">
        <textarea
          ref={inputRef}
          className="ais-chat-input"
          value={inputText}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={labels.get('input.placeholder')}
          rows={1}
          disabled={loading}
        />
        <button
          ref={sendBtnRef}
          className="ais-chat-send-btn"
          onClick={handleSend}
          title={labels.get('button.send')}
          disabled={!inputText.trim() || loading}
        >
          <SendIcon />
        </button>
      </div>

      <div className="ais-chat-disclaimer">{labels.get('disclaimer')}</div>
    </div>
  );
}

AiAssistantChat.propTypes = {
  sData: PropTypes.object.isRequired,
  isVisible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
